import React, { Component } from 'react';
import { Row, Col, Card, CardBody, Button, Badge } from 'reactstrap';
import axios from 'axios';
import { FaClock, FaCheckCircle, FaTimesCircle, FaUserClock } from 'react-icons/fa';
import "./dashboard.scss";
import { API_BASE_URL } from '../../constants';

class AdminAttendance extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            message: '',
            status: '', 
            checkInTime: null,
            checkOutTime: null,
            elapsed: '',
            showMessage: false
        };
        this.timerInterval = null;
        this.messageTimeout = null;
    }

    componentDidMount() {
        this.getAttendanceStatus();
    }

    componentWillUnmount() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
        if (this.messageTimeout) {
            clearTimeout(this.messageTimeout);
        }
    }

    formatDuration = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins
            .toString()
            .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    formatTime = (timeString) => {
        if (!timeString) return '';
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const formattedHour = hour % 12 || 12;
        return `${formattedHour}:${minutes} ${ampm}`;
    };

    startTimer = (checkIn, checkOut = null) => {
        if (this.timerInterval) clearInterval(this.timerInterval);
        
        // Only start timer if checked in but not checked out
        if (checkIn && !checkOut && this.state.status === 'checked_in') {
            const checkInDate = new Date();
            const [h, m, s] = checkIn.split(':');
            checkInDate.setHours(h, m, s, 0);

            this.timerInterval = setInterval(() => {
                const now = new Date();
                const diff = Math.floor((now - checkInDate) / 1000);
                this.setState({ elapsed: this.formatDuration(diff) });
            }, 1000);
        }
    };

    getAttendanceStatus = async () => {
        this.setState({ loading: true });
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/my-attendance`, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token")
                }
            });

            if (response.data.status === 'success') {
                const attendance = response.data.attendance;
                let status = 'not_marked';

                if (attendance) {
                    const { check_in, check_out } = attendance;
                    if (check_in && !check_out) status = 'checked_in';
                    else if (check_in && check_out) status = 'checked_out';

                    this.setState({ 
                        checkInTime: check_in, 
                        checkOutTime: check_out,
                        status
                    });

                    if (status === 'checked_in') {
                        this.startTimer(check_in);
                    }
                } else {
                    this.setState({ status });
                }
            }
        } catch (error) {
            console.error('Error fetching attendance status:', error);
        } finally {
            this.setState({ loading: false });
        }
    }

    handleAttendance = async () => {
        this.setState({ loading: true, message: '' });

        try {
            const response = await axios.post(`${API_BASE_URL}/attendance/mark`, {}, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token")
                }
            });

            if (response.data.status === 'success' || response.data.status === 'info') {
                this.setState({ 
                    message: response.data.message,
                    showMessage: true
                });
                
                // Auto-hide message after 5 seconds
                if (this.messageTimeout) {
                    clearTimeout(this.messageTimeout);
                }
                this.messageTimeout = setTimeout(() => {
                    this.setState({ showMessage: false });
                }, 5000);
                
                this.getAttendanceStatus(); // Refresh status
            } else {
                this.setState({ 
                    message: response.data.message,
                    showMessage: true
                });
            }
        } catch (error) {
            this.setState({ 
                message: 'Error marking attendance. Please try again.',
                showMessage: true
            });
            console.error(error);
        } finally {
            this.setState({ loading: false });
        }
    }

    getStatusBadge = () => {
        const { status } = this.state;
        switch (status) {
            case 'checked_in':
                return <Badge color="success" className="p-2"><FaCheckCircle className="mr-1" /> Checked In</Badge>;
            case 'checked_out':
                return <Badge color="info" className="p-2"><FaTimesCircle className="mr-1" /> Checked Out</Badge>;
            case 'not_marked':
            default:
                return <Badge color="warning" className="p-2"><FaUserClock className="mr-1" /> Attendance Not Marked</Badge>;
        }
    }

    getButtonText = () => {
        const { status, loading } = this.state;
        if (loading) return 'Processing...';
        
        switch (status) {
            case 'checked_in':
                return 'Check Out';
            case 'checked_out':
                return 'Mark New Attendance';
            case 'not_marked':
            default:
                return 'Check In';
        }
    }

    getButtonColor = () => {
        const { status } = this.state;
        switch (status) {
            case 'checked_in':
                return 'danger';
            case 'checked_out':
                return 'primary';
            case 'not_marked':
            default:
                return 'primary';
        }
    }

    render() {
        const { loading, message, elapsed, status, checkInTime, checkOutTime, showMessage } = this.state;

        return (
            <Card className="shadow-sm">
                <CardBody>
                    <div className="text-center mb-4">
                        <h4 className="card-title">Daily Attendance</h4>
                        <div className="mt-3 mb-3">
                            {this.getStatusBadge()}
                        </div>
                    </div>

                    <Row className="attendance-details">
                        <Col md={6} className="border-right">
                            <div className="text-center py-3">
                                <h6 className="text-muted">CHECK IN</h6>
                                <h5>{checkInTime ? this.formatTime(checkInTime) : '--:--'}</h5>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="text-center py-3">
                                <h6 className="text-muted">CHECK OUT</h6>
                                <h5>{checkOutTime ? this.formatTime(checkOutTime) : '--:--'}</h5>
                            </div>
                        </Col>
                    </Row>

                    {status === 'checked_in' && (
                        <div className="text-center my-4 elapsed-time">
                            <div className="d-flex align-items-center justify-content-center">
                                <FaClock className="text-primary mr-2" />
                                <h5 className="mb-0">Elapsed Time: <span className="text-primary">{elapsed}</span></h5>
                            </div>
                        </div>
                    )}

                    <div className="text-center mt-4">
                        <Button
                            color={this.getButtonColor()}
                            size="lg"
                            className="px-5 py-2"
                            onClick={this.handleAttendance}
                            disabled={loading}
                        >
                            {this.getButtonText()}
                        </Button>
                    </div>

                    {showMessage && message && (
                        <div className="mt-4">
                            <div className="alert alert-info fade show">
                                {message}
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>
        );
    }
}

export default AdminAttendance;